import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CheckCircle2, Gift, X, Loader2, FileText, Sunrise, Ticket } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'nm_freebie_seen';

const OPTIONS = [
  { key: 'pcos_starter', label: '7-Day PCOS Starter Guide', desc: 'A dietitian-crafted PDF plan sent instantly.', icon: FileText },
  { key: 'morning_routine', label: '10-Min Morning Routine', desc: 'The metabolism-boosting stack our clients swear by.', icon: Sunrise },
  { key: 'free_consultation', label: 'Free 15-min Consult', desc: 'Unique coupon for a real dietitian call.', icon: Ticket },
];

const LeadMagnetPopup = () => {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState('pcos_starter');
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    // Timer trigger
    const timer = setTimeout(() => setOpen(true), 15000);

    // Exit-intent trigger (desktop only)
    const onMouseLeave = (e) => {
      if (e.clientY <= 5 && !localStorage.getItem(STORAGE_KEY)) {
        setOpen(true);
      }
    };
    document.addEventListener('mouseleave', onMouseLeave);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/leads`, { ...form, freebie: choice, source: 'popup' }, { withCredentials: true });
      setResult(r.data);
      localStorage.setItem(STORAGE_KEY, '1');
    } catch (err) {
      setResult({ ok: false, message: err?.response?.data?.detail || 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const activeOpt = OPTIONS.find((o) => o.key === choice);

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : dismiss())}>
      <DialogContent className="sm:max-w-2xl p-0 overflow-hidden rounded-3xl border-none bg-[#faf7f1]">
        {!result ? (
          <div className="grid md:grid-cols-2">
            {/* Left visual */}
            <div className="hidden md:block relative bg-gradient-to-br from-[#3a5a2c] to-[#4d6f3d] p-10 text-white">
              <Gift className="w-9 h-9 mb-4 text-[#f4d5a8]" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#f4d5a8] mb-2">Free gift</div>
              <h2 className="font-serif text-3xl leading-tight">Wait — before you go, take this with you.</h2>
              <p className="mt-4 text-white/80 text-sm leading-relaxed">
                Pick one of three dietitian-crafted freebies below. We&apos;ll send it to your email in the next few minutes.
              </p>
              <div className="mt-8 space-y-3">
                {['No spam ever', 'Unsubscribe with 1 click', 'Made by real dietitians'].map((t) => (
                  <div key={t} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#f4d5a8]" /> {t}
                  </div>
                ))}
              </div>
            </div>

            {/* Right form */}
            <div className="p-8 relative">
              <button onClick={dismiss} className="absolute right-4 top-4 w-8 h-8 rounded-full hover:bg-neutral-100 flex items-center justify-center text-[#7a8471]">
                <X className="w-4 h-4" />
              </button>

              <div className="md:hidden mb-4">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#c9743a] mb-1">Free gift</div>
                <h2 className="font-serif text-2xl text-[#1a2b17] leading-tight">Grab yours before you go</h2>
              </div>

              <div className="space-y-2 mb-4">
                {OPTIONS.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setChoice(o.key)}
                    className={`w-full text-left p-3 rounded-2xl border-2 flex items-center gap-3 transition ${
                      choice === o.key ? 'border-[#3a5a2c] bg-white' : 'border-neutral-200 hover:border-[#3a5a2c] bg-white/60'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${choice === o.key ? 'bg-[#3a5a2c] text-white' : 'bg-[#faf7f1] text-[#3a5a2c]'}`}>
                      <o.icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#1a2b17]">{o.label}</div>
                      <div className="text-xs text-[#7a8471] truncate">{o.desc}</div>
                    </div>
                  </button>
                ))}
              </div>

              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label className="text-xs">Full name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-10 rounded-xl bg-white" placeholder="Jane Doe" />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-10 rounded-xl bg-white" placeholder="you@email.com" />
                </div>
                <div>
                  <Label className="text-xs">Phone (optional)</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-10 rounded-xl bg-white" placeholder="+91 95454 05936" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-[#3a5a2c] hover:bg-[#2d4622] text-white mt-2">
                  {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : `Send me the ${activeOpt.label.split(' ')[0]}`}
                </Button>
                <button type="button" onClick={dismiss} className="w-full text-xs text-[#7a8471] hover:text-[#1a2b17]">No thanks, maybe later</button>
              </form>
            </div>
          </div>
        ) : (
          <div className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-[#e8f2e2] mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#3a5a2c]" />
            </div>
            <h2 className="font-serif text-3xl text-[#1a2b17]">Thank you, {form.name.split(' ')[0]}!</h2>
            <p className="mt-3 text-[#4a5544]">{result.message || 'Your freebie is on its way.'}</p>
            {choice === 'pcos_starter' && (
              <a
                href="/pdfs/nutrifyme-pcos-guide.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#3a5a2c] hover:bg-[#2d4622] text-white font-medium transition"
              >
                Download PCOS Guide (PDF)
              </a>
            )}
            {result.coupon_code && (
              <div className="mt-6 inline-block px-6 py-4 rounded-2xl bg-[#fce7d5] border border-[#c9743a]/30">
                <div className="text-[10px] tracking-widest uppercase text-[#c9743a] mb-1">Your coupon</div>
                <div className="font-mono text-2xl text-[#1a2b17] tracking-widest">{result.coupon_code}</div>
                <div className="text-xs text-[#4a5544] mt-2">Mention this on your free 15-min consultation call.</div>
              </div>
            )}
            <a
              href={`https://wa.me/919545405936?text=${encodeURIComponent(`Hi, I signed up for the ${OPTIONS.find(o => o.key === choice)?.label || 'freebie'}.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#25D366] hover:bg-[#20b45a] text-white font-medium transition"
            >
              Get it on WhatsApp now
            </a>
            <div className="mt-4 text-[10px] tracking-widest uppercase text-[#7a8471]">Email delivery active</div>
            <Button onClick={dismiss} variant="outline" className="mt-6 rounded-full">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LeadMagnetPopup;
